// frontend\src\pages\AboutPage\AboutPage.js
import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { APP_NAME } from "../../constants";
import Loader from "../../components/Loader";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import { toast } from "react-toastify";
import ImageModal from "./ImageModal";
import ImageWithFallback from "./ImageWithFallback";
import Breadcrumbs from "./Breadcrumbs";

const AboutPage = () => {
  const [markdown, setMarkdown] = useState("");
  const [currentPage, setCurrentPage] = useState("/content/about.md");
  const [history, setHistory] = useState(["/content/about.md"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [contentKey, setContentKey] = useState(0); // Добавляем ключ для сброса состояния

  // Функция для нормализации пути к MD-файлу
  const normalizePath = (basePath, linkPath) => {
    console.log('Normalizing path:', { basePath, linkPath });
    
    if (linkPath.startsWith('/content/')) {
      return linkPath;
    }
    
    if (linkPath.startsWith('../') || linkPath.startsWith('./')) {
      const baseDir = basePath.substring(0, basePath.lastIndexOf('/'));
      let result = baseDir;
      
      if (linkPath.startsWith('../')) {
        const parts = linkPath.split('/');
        let backLevels = 0;
        
        while (parts[0] === '..') {
          backLevels++;
          parts.shift();
        }
        
        for (let i = 0; i < backLevels; i++) {
          result = result.substring(0, result.lastIndexOf('/'));
        }
        
        if (parts.length > 0) {
          result += '/' + parts.join('/');
        }
      } else if (linkPath.startsWith('./')) {
        result += linkPath.substring(1);
      }
      
      console.log('Normalized relative path:', result);
      return result;
    }
    
    const baseDir = basePath.substring(0, basePath.lastIndexOf('/'));
    const result = `${baseDir}/${linkPath}`;
    console.log('Normalized absolute path:', result);
    return result;
  };

  // Функция для загрузки markdown
  const loadMarkdown = async (url) => {
    try {
      console.log('Loading markdown from:', url);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to load: ${url}`);
      let text = await response.text();
      text = text.replace(/\{APP_NAME\}/g, APP_NAME);
      console.log('Markdown loaded successfully, length:', text.length);
      return text;
    } catch (err) {
      console.error("Error loading markdown:", err);
      throw err;
    }
  };

  // Функция для определения пути к изображениям
  const getImageBasePath = (currentPath) => {
    const basePath = currentPath.substring(0, currentPath.lastIndexOf('/') + 1);
    console.log('Image base path for', currentPath, 'is:', basePath);
    return basePath;
  };

  // Загрузка контента
  useEffect(() => {
    const loadContent = async () => {
      try {
        console.log('=== STARTING PAGE LOAD ===', currentPage);
        setLoading(true);
        setError(null);
        setSelectedImage(null);
        setIsNavigating(true);
        
        // Очищаем предыдущий контент и сбрасываем ключ
        console.log('Clearing previous markdown content');
        setMarkdown("");
        setContentKey(prev => prev + 1); // Сбрасываем ключ для принудительного пересоздания
        
        // Даем время для очистки DOM
        await new Promise(resolve => setTimeout(resolve, 10));
        
        const content = await loadMarkdown(currentPage);
        console.log('Setting new markdown content');
        setMarkdown(content);
        
        setIsNavigating(false);
        window.scrollTo(0, 0);
        console.log('=== PAGE LOAD COMPLETE ===', currentPage);
      } catch (err) {
        console.error("Error loading page:", err);
        toast.error('Не удалось загрузить содержимое страницы');
        setError("Не удалось загрузить содержимое страницы");
        setIsNavigating(false);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [currentPage]);

  // Навигация по ссылкам
  const handleNavigation = (path) => {
    try {
      console.log('Navigation requested to:', path);
      setIsNavigating(true);
      const normalizedPath = normalizePath(currentPage, path);
      console.log('Normalized path:', normalizedPath);
      setCurrentPage(normalizedPath);
      setHistory(prev => [...prev, normalizedPath]);
    } catch (err) {
      console.error("Navigation error:", err);
      toast.error('Ошибка навигации');
      setIsNavigating(false);
    }
  };

  // Назад по истории
  const goBack = () => {
    if (history.length > 1) {
      const newHistory = history.slice(0, -1);
      const previousPath = newHistory[newHistory.length - 1];
      console.log('Going back to:', previousPath);
      setCurrentPage(previousPath);
      setHistory(newHistory);
    } else {
      toast.info('Вы на первой странице');
    }
  };

  // Домашняя страница
  const goHome = () => {
    console.log('Going home');
    setCurrentPage("/content/about.md");
    setHistory(["/content/about.md"]);
  };

  if (loading) {
    return <Loader />;
  }

  const imageBasePath = getImageBasePath(currentPage);

  return (
    <div className="container mt-4">
      <ImageModal 
        image={selectedImage} 
        onClose={() => setSelectedImage(null)} 
      />

      <nav className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <button 
              onClick={goHome}
              className="btn btn-outline-primary btn-sm me-2"
            >
              <i className="bi bi-house me-1"></i>
              Главная
            </button>
            <button 
              onClick={goBack} 
              disabled={history.length <= 1}
              className="btn btn-outline-secondary btn-sm"
            >
              <i className="bi bi-arrow-left me-1"></i>
              Назад
            </button>
          </div>
        </div>
        
        <Breadcrumbs 
          currentPage={currentPage} 
          onNavigate={handleNavigation} 
        />
      </nav>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <div className="mt-2">
            <button onClick={goHome} className="btn btn-primary btn-sm me-2">
              <i className="bi bi-house me-1"></i>
              На главную
            </button>
            <button onClick={goBack} className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-arrow-left me-1"></i>
              Назад
            </button>
          </div>
        </div>
      )}

      {/* Показываем Loader во время навигации */}
      {isNavigating && (
        <div className="d-flex justify-content-center my-5">
          <Loader />
        </div>
      )}

      {/* Рендерим контент только когда НЕ в процессе навигации */}
      {!error && !isNavigating && markdown && (
        <div className="markdown-content" key={contentKey}>
          <ReactMarkdown
            key={`${currentPage}-${contentKey}`}
            components={{
              img: ({ src, alt }) => {
                if (!src) return null;
                
                const imageUrl = `${imageBasePath}${src}`;
                
                // Проверка: изображение должно существовать в текущей директории
                const currentDir = currentPage.substring(0, currentPage.lastIndexOf('/'));
                const imageBelongsToCurrentPage = imageUrl.startsWith(currentDir + '/');
                
                if (!imageBelongsToCurrentPage) {
                  console.log('BLOCKING IMAGE FROM WRONG PAGE:', imageUrl, 'currentPage:', currentPage);
                  return null;
                }
                
                console.log('ALLOWING IMAGE:', imageUrl);
                
                return (
                  <ImageWithFallback 
                    key={`${src}-${imageBasePath}-${contentKey}`}
                    src={src} 
                    alt={alt} 
                    imageBasePath={imageBasePath}
                    onImageClick={setSelectedImage}
                  />
                );
              },
              a: ({ href, children }) => {
                console.log('ReactMarkdown rendering link:', { href, children });
                
                if (!href) return <span>{children}</span>;
                
                if (href.endsWith('.md') || href.includes('/content/')) {
                  return (
                    <button 
                      onClick={() => {
                        try {
                          console.log('Internal link clicked:', href);
                          handleNavigation(href);
                        } catch (err) {
                          console.error("Link navigation error:", err);
                          toast.error('Ошибка перехода по ссылке');
                        }
                      }}
                      className="btn btn-link p-0 text-decoration-none border-0"
                      style={{ 
                        color: 'var(--btn-outline-primary-color)',
                        background: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      {children} 
                      <i className="bi bi-arrow-up-right ms-1 small"></i>
                    </button>
                  );
                }
                
                return (
                  <a 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    style={{ color: 'var(--btn-outline-primary-color)' }}
                    onClick={() => {
                      console.log('External link clicked:', href);
                      toast.info('Открывается внешняя ссылка');
                    }}
                  >
                    {children} 
                    <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                  </a>
                );
              },
              h1: ({ children }) => (
                <h1 className="border-bottom pb-2 mb-4" style={{ color: 'var(--text-primary)' }}>
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="border-bottom pb-2 mt-4 mb-3" style={{ color: 'var(--text-primary)' }}>
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mt-3 mb-2" style={{ color: 'var(--text-primary)' }}>
                  {children}
                </h3>
              ),
              p: ({ children, node }) => {
                // Исправление: если внутри параграфа есть блоковые элементы, оборачиваем в div
                const hasBlockElements = node?.children?.some(child => 
                  child.type === 'element' && 
                  ['div', 'img', 'pre', 'table', 'ul', 'ol', 'blockquote'].includes(child.tagName)
                );
                
                if (hasBlockElements) {
                  return <div className="my-3">{children}</div>;
                }
                
                return <p className="my-3">{children}</p>;
              },
              code: ({ children, className }) => {
                const isInline = !className;
                if (isInline) {
                  return (
                    <code 
                      className="px-1 py-1 rounded small"
                      style={{ 
                        backgroundColor: 'var(--bg-secondary)', 
                        color: 'var(--text-primary)' 
                      }}
                    >
                      {children}
                    </code>
                  );
                }
                return (
                  <div className="my-3">
                    <pre 
                      className="p-3 rounded"
                      style={{ 
                        backgroundColor: 'var(--bg-dark)', 
                        color: 'var(--text-light)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      <code>{children}</code>
                    </pre>
                  </div>
                );
              },
              blockquote: ({ children }) => (
                <blockquote 
                  className="border-start ps-3 ms-3 my-3"
                  style={{ 
                    borderLeftColor: 'var(--btn-primary-bg)',
                    color: 'var(--text-secondary)',
                    backgroundColor: 'var(--bg-secondary)',
                    padding: '1rem 1.5rem',
                    borderRadius: '0 0.375rem 0.375rem 0'
                  }}
                >
                  {children}
                </blockquote>
              ),
              ul: ({ children }) => (
                <ul className="my-3" style={{ color: 'var(--text-primary)' }}>
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="my-3" style={{ color: 'var(--text-primary)' }}>
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="mb-1" style={{ color: 'var(--text-primary)' }}>
                  {children}
                </li>
              ),
              table: ({ children }) => (
                <div className="table-responsive my-3">
                  <table className="table table-striped">{children}</table>
                </div>
              )
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      )}

      <ScrollToTopButton />

      {history.length > 1 && !isNavigating && (
        <div className="mt-5 pt-3 border-top mb-5">
          <button 
            onClick={goBack}
            className="btn btn-outline-secondary"
          >
            <i className="bi bi-arrow-left me-1"></i>
            Вернуться назад
          </button>
        </div>
      )}
    </div>
  );
};

export default AboutPage;